import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { Box, Text } from "@chakra-ui/react";
import { SCOPE_COLORS } from "./KpiCharts";

// d3 icicle chart — top-to-bottom bands per depth level (root → scope →
// category → sub-category), click to zoom into any segment, click the top
// bar to zoom back out. Colours match the rest of the insights page (lime
// for Scope 1, blue for Scope 2, purple for Scope 3).

const HEIGHT = 380;
const ROOT_FILL = "#E8E6E0";
const TEXT_DARK = "#1A202C";
const TEXT_LIGHT = "#FFFFFF";

// Lighten a hex colour towards white. Used to step from each scope's base
// colour through progressively paler tints for child / grand-child rows.
const lighten = (hex, amount) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  const mix = (c) => Math.round(c + (255 - c) * amount);
  return `#${[mix(r), mix(g), mix(b)].map((n) => n.toString(16).padStart(2, "0")).join("")}`;
};

// Per-scope shade ramp. Index 0 = scope row, 1 = category row, 2 = sub-cat.
const SCOPE_SHADES = {
  "Scope 1": [SCOPE_COLORS.scope1.base, lighten(SCOPE_COLORS.scope1.base, 0.35), lighten(SCOPE_COLORS.scope1.base, 0.65)],
  "Scope 2": [SCOPE_COLORS.scope2.base, lighten(SCOPE_COLORS.scope2.base, 0.35), lighten(SCOPE_COLORS.scope2.base, 0.65)],
  "Scope 3": [SCOPE_COLORS.scope3.base, lighten(SCOPE_COLORS.scope3.base, 0.35), lighten(SCOPE_COLORS.scope3.base, 0.65)],
};

// Match by the leading "Scope N" so labelled variants (e.g.
// "Scope 2 (Market-based)") still resolve to the right colour ramp.
const rampForScopeName = (name) => {
  if (SCOPE_SHADES[name]) return SCOPE_SHADES[name];
  const match = Object.keys(SCOPE_SHADES).find((k) => String(name || "").startsWith(k));
  return match ? SCOPE_SHADES[match] : ["#888", "#aaa", "#ccc"];
};

const fillForNode = (d) => {
  if (d.depth === 0) return ROOT_FILL;
  let n = d;
  while (n.depth > 1) n = n.parent;
  const ramp = rampForScopeName(n.data.name);
  // depth 1 → ramp[0]; depth 2 → ramp[1]; depth 3+ → ramp[2]
  return ramp[Math.min(d.depth - 1, ramp.length - 1)];
};

// Choose dark/light text against the segment fill. Simple luminance test.
const textColorFor = (fill) => {
  const h = fill.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  // Rec. 709 luminance.
  const l = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return l > 0.6 ? TEXT_DARK : TEXT_LIGHT;
};

// Render the segment value at full precision (matches the SummaryCards
// tiles above). Locale-formatted thousands separators + two decimals so
// the figure here reconciles exactly against the headline KPI cards.
const formatValue = (v) => {
  const n = Number(v) || 0;
  return `${n.toLocaleString("en-GB", { maximumFractionDigits: 2 })} tCO2e`;
};

function EmissionsIcicle({ data }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const widthRef = useRef(0);
  const focusRef = useRef(null);
  // Hover state lives in React (we render the tooltip as a Chakra Box) so it
  // can sit on top of the SVG without the d3 render path needing to know
  // about it.
  const [hover, setHover] = useState(null);

  // Pre-compute the partition layout once the data shape changes. We keep
  // d3's internal x0/x1/y0/y1 in a normalised [0..1] space so the focus
  // animation can zoom independently of the container's pixel width.
  const root = useMemo(() => {
    if (!data) return null;
    const r = d3
      .hierarchy(data)
      .sum((d) => d.value || 0)
      .sort((a, b) => (b.value || 0) - (a.value || 0));
    d3
      .partition()
      .size([1, r.height + 1])(r);
    r.each((d) => {
      // Persist starting target — we mutate `current` during transitions.
      d.current = { x0: d.x0, x1: d.x1, y0: d.y0, y1: d.y1 };
      d.target = { ...d.current };
    });
    return r;
  }, [data]);

  // Initial render + subsequent re-renders on data change.
  useEffect(() => {
    if (!root || !svgRef.current || !containerRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    focusRef.current = root;

    const rowHeight = HEIGHT / (root.height + 1);

    const groups = svg
      .append("g")
      .selectAll("g")
      .data(root.descendants())
      .join("g")
      .attr("class", "icicle-segment")
      .attr("data-depth", (d) => d.depth)
      .style("cursor", "pointer");

    // Per-segment clipPath so text never bleeds beyond the rect.
    groups
      .append("clipPath")
      .attr("id", (d, i) => `icicle-clip-${i}`)
      .append("rect")
      .attr("class", "clip-rect");

    groups
      .append("rect")
      .attr("class", "tile")
      .attr("rx", 3)
      .attr("ry", 3)
      .attr("fill", (d) => fillForNode(d))
      .attr("stroke", "#ffffff")
      .attr("stroke-width", 1);

    const labels = groups
      .append("g")
      .attr("class", "label")
      .attr("clip-path", (d, i) => `url(#icicle-clip-${i})`)
      .style("pointer-events", "none");

    labels
      .append("text")
      .attr("class", "name")
      .attr("font-size", 12)
      .attr("font-weight", 600)
      .attr("dominant-baseline", "central");

    labels
      .append("text")
      .attr("class", "meta")
      .attr("font-size", 11)
      .attr("font-weight", 500)
      .attr("dominant-baseline", "central");

    groups.on("click", (event, d) => {
      // Click the top bar (current focus) to zoom back to its parent;
      // click any other segment to zoom into it.
      const next = d === focusRef.current ? d.parent || root : d;
      focusRef.current = next;
      runTransition(next);
    });

    groups
      .on("mousemove", (event, d) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const path = d.ancestors().reverse().slice(1).map((n) => n.data.name);
        const value = d.value || 0;
        const parentValue = d.parent?.value || 0;
        const pct = parentValue > 0 ? (value / parentValue) * 100 : null;
        setHover({
          // Pointer position in container-local coords. Final placement
          // (left vs right of cursor, clamping) is computed during render
          // so the tooltip never overflows the chart's bounds.
          mouseX: event.clientX - rect.left,
          mouseY: event.clientY - rect.top,
          containerW: rect.width,
          // Skip the synthetic root in the path label — it just says "Total".
          path: path.length ? path.join(" · ") : d.data.name,
          value,
          pct,
          parentName: d.parent?.data?.name,
        });
      })
      .on("mouseleave", () => setHover(null));

    const layout = () => {
      const width = widthRef.current || 800;
      groups.each(function (d) {
        const c = d.current;
        const x = c.x0 * width;
        const y = c.y0 * rowHeight;
        const w = Math.max(0, (c.x1 - c.x0) * width - 1);
        const h = Math.max(0, (c.y1 - c.y0) * rowHeight - 1);
        const sel = d3.select(this);
        sel.attr("transform", `translate(${x}, ${y})`);
        sel.select("rect.tile").attr("width", w).attr("height", h);
        sel.select("rect.clip-rect").attr("width", w).attr("height", h);

        const name = d.data.name;
        const value = d.value || 0;
        const parentValue = d.parent?.value || 0;
        const pct = parentValue > 0 ? (value / parentValue) * 100 : 0;
        const fill = fillForNode(d);
        const textFill = textColorFor(fill);

        const labelGroup = sel.select("g.label");
        const nameText = labelGroup.select("text.name");
        const metaText = labelGroup.select("text.meta");

        if (w < 60 || h < 16) {
          nameText.style("display", "none");
          metaText.style("display", "none");
        } else if (w < 120) {
          nameText
            .style("display", null)
            .attr("x", 8)
            .attr("y", h / 2)
            .attr("fill", textFill)
            .text(name);
          metaText.style("display", "none");
        } else {
          nameText
            .style("display", null)
            .attr("x", 10)
            .attr("y", h >= 36 ? h / 2 - 8 : h / 2)
            .attr("fill", textFill)
            .text(name);
          if (h >= 36) {
            metaText
              .style("display", null)
              .attr("x", 10)
              .attr("y", h / 2 + 9)
              .attr("fill", textFill)
              .attr("opacity", 0.85)
              .text(`${formatValue(value)}${parentValue > 0 ? ` · ${pct.toFixed(1)}%` : ""}`);
          } else {
            metaText.style("display", "none");
          }
        }
      });
    };

    const runTransition = (focus) => {
      // Compute target [x0..x1] mapping so the focused segment fills the
      // full width and its descendants scale relative to it. The vertical
      // axis stays fixed.
      root.each((d) => {
        const x0 = (d.x0 - focus.x0) / (focus.x1 - focus.x0);
        const x1 = (d.x1 - focus.x0) / (focus.x1 - focus.x0);
        d.target = {
          x0: Math.max(0, Math.min(1, x0)),
          x1: Math.max(0, Math.min(1, x1)),
          y0: d.y0,
          y1: d.y1,
        };
      });

      const t = svg.transition().duration(650).ease(d3.easeCubic);
      groups
        .transition(t)
        .tween("layout", function (d) {
          const i = d3.interpolate(d.current, d.target);
          return (tt) => {
            d.current = i(tt);
            // We mutate inside the tween then call layout once per frame
            // outside so we don't re-select for every node.
          };
        });
      // On every animation frame we still need to push the new positions
      // to the DOM. Use an attrTween-driven master tween on the SVG.
      svg
        .transition(t)
        .tween("redraw", () => () => layout());
    };

    // Defer to the next frame so we don't synchronously mutate the DOM in
    // response to a resize and trigger Chrome's "ResizeObserver loop
    // completed with undelivered notifications" warning (which webpack-dev
    // surfaces as an uncaught error overlay).
    let rafId = null;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width || 0;
      if (w === 0) return;
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = null;
        widthRef.current = w;
        svg.attr("width", w).attr("height", HEIGHT).attr("viewBox", `0 0 ${w} ${HEIGHT}`);
        layout();
      });
    });
    ro.observe(containerRef.current);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [root]);

  return (
    <Box w="100%">
      <Text fontSize="12px" color="gray.500" mb={2}>
        Click any segment to drill down. Click it again to step back up.
      </Text>
      <Box ref={containerRef} w="100%" position="relative">
        <svg ref={svgRef} style={{ display: "block", width: "100%", height: HEIGHT }} />
      {(() => {
        if (!hover) return null;
        const TIP_W = 260;
        const offset = 12;
        // If putting the tooltip to the right of the cursor would overflow
        // the chart, anchor it to the cursor's left instead. Clamp the
        // top so it stays inside the 380px chart height.
        const fitsRight = hover.mouseX + offset + TIP_W <= hover.containerW;
        const left = fitsRight
          ? hover.mouseX + offset
          : Math.max(0, hover.mouseX - offset - TIP_W);
        const top = Math.min(Math.max(0, hover.mouseY + offset), HEIGHT - 60);
        return (
          <Box
            position="absolute"
            left={`${left}px`}
            top={`${top}px`}
            w={`${TIP_W}px`}
            bg="white"
            px={3}
            py={2}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            boxShadow="sm"
            pointerEvents="none"
            zIndex={10}
          >
            <Text fontSize="sm" fontWeight="bold" color="gray.700" noOfLines={2}>
              {hover.path}
            </Text>
            <Text fontSize="sm" color="gray.600">
              {formatValue(hover.value)}
              {hover.pct != null && (
                <Text as="span" color="gray.400" ml={2}>
                  · {hover.pct.toFixed(1)}% of {hover.parentName === "Total" ? "total" : hover.parentName}
                </Text>
              )}
            </Text>
          </Box>
        );
      })()}
      </Box>
    </Box>
  );
}

export default EmissionsIcicle;
